import { Maybe } from 'graphql/jsutils/Maybe'
import { Episode, EpisodeLinkMeta, Project, ProjectType } from '@/services/ProjectsService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { getContinueWatchingCta, getContinueWatchingCtaNew, getDefaultCta, getTrailerCta } from '@/utils/EpisodeUtil'
import { TranslateFunction } from '@/utils/translate/translate-client'

export const getResumeText = (canShowGuildHero: boolean, t: TranslateFunction) => {
  return canShowGuildHero ? t('continue', 'Continue') : t('resume', 'Resume')
}

export const getResumeCta = ({
  currentEpisode,
  locale,
  projectType,
  resume,
  slug,
  canShowGuildHero,
}: {
  currentEpisode: Episode
  locale: string
  projectType: ProjectType
  resume: string
  slug: string
  canShowGuildHero: boolean
}) => {
  return canShowGuildHero
    ? getContinueWatchingCtaNew({ currentEpisode, locale, projectType, resume, slug })
    : getContinueWatchingCta({ currentEpisode, locale, projectType, resume, slug })
}

export const getRecapCta = ({
  currentEpisode,
  locale,
  slug,
  description,
  t,
}: {
  currentEpisode?: Episode
  locale: string
  slug: string
  description: string
  t: TranslateFunction
}) => {
  const ctaText = t('watchRecap', 'Watch Recap')
  return getTrailerCta({ ctaText, currentEpisode, locale, slug, description })
}

export const getSneakPeekCta = ({
  currentEpisode,
  locale,
  slug,
  description,
  contentfulProjectTheme,
  t,
}: {
  currentEpisode?: Episode
  locale: string
  slug: string
  description: string
  contentfulProjectTheme: Maybe<ProjectTheme>
  t: TranslateFunction
}) => {
  const ctaText = contentfulProjectTheme?.sneakPeekDuration
    ? t('watchFirstMinutes', `Watch The First {{ minutes }} Minutes`, {
        minutes: contentfulProjectTheme.sneakPeekDuration,
      })
    : t('watchSneakPeek', 'Watch Sneak Peek')
  return getTrailerCta({ ctaText, currentEpisode, locale, slug, description })
}

export const getTrailerCtaProps = ({
  currentEpisode,
  locale,
  slug,
  description,
  t,
}: {
  currentEpisode?: Episode
  locale: string
  slug: string
  description: string
  t: TranslateFunction
}) => {
  const ctaText = t('watchTrailer', 'Watch Trailer')
  return getTrailerCta({ ctaText, currentEpisode, locale, slug, description })
}

export const getDefaultTrailerCta = ({
  projectData,
  slug,
  description,
  t,
}: {
  projectData: Project
  slug: string
  description: string
  t: TranslateFunction
}) => {
  return {
    showDescription: description,
    ctaPath: `/watch/${slug}/videos/trailers/${projectData?.trailers?.[0]?.id}`,
    ctaText: t('watchTrailer', 'Watch Trailer'),
  }
}

export const getEarlyAccessCta = ({
  slug,
  description,
  episodeTrailer,
  t,
}: {
  slug: string
  description: string
  episodeTrailer: EpisodeLinkMeta
  t: TranslateFunction
}) => {
  return {
    showDescription: description,
    ctaPath: `/watch/${slug}/episode/${episodeTrailer.id}`,
    ctaText: t('watchTrailer', 'Watch Trailer'),
  }
}

export const getWatchNowCta = ({
  currentEpisode,
  locale,
  slug,
  description,
  t,
}: {
  currentEpisode?: Episode
  locale: string
  slug: string
  description: string
  t: TranslateFunction
}) => {
  const ctaText = t('watchNow', 'Watch Now')
  return getDefaultCta({ ctaText, currentEpisode, locale, slug, description })
}

export const getMovieEpisodeSharePath = ({
  slug,
  guid,
  projectType,
  defaultCtaPath,
}: {
  slug: string
  guid: string | null
  projectType: ProjectType
  defaultCtaPath: string
}): string | null => {
  // For series, continue to use the share modal entry point
  if (projectType === 'series') return '#share-series-modal'

  // For movies and specials, construct the share link with the provided guid when available
  if (guid) return `/watch/${slug}/episode/${guid}?share=true`

  // Fallback to default cta path when guid is unavailable
  return defaultCtaPath || null
}

export const shouldShowGuildHero = ({
  canShowTickets,
  isProjectInEarlyAccess,
  isLoggedIn,
  isGuildMember,
  projectType,
  watchableReasons,
}: {
  canShowTickets: boolean
  isProjectInEarlyAccess: boolean
  isLoggedIn: boolean
  isGuildMember: boolean
  projectType: ProjectType
  watchableReasons?: Array<{
    __typename: string
    startDatetime?: string | null
  } | null> | null
}): boolean => {
  if (canShowTickets || !isProjectInEarlyAccess) {
    return false
  }

  if (!isLoggedIn) {
    return true
  }

  if (!isGuildMember) {
    if (projectType === 'series') {
      return true
    }

    if (projectType === 'movie') {
      return !watchableReasons || watchableReasons.length === 0
    }
  }

  return false
}
